const PIX_GUI = 'br.gov.bcb.pix';
const MERCHANT_CITY = 'SAO PAULO';
const NAME_FALLBACK = 'CLIPADOR';

function emv(id: string, value: string): string {
  return `${id}${String(value.length).padStart(2, '0')}${value}`;
}

/**
 * CRC-16/CCITT-FALSE (init 0xFFFF, poly 0x1021, no reflection) — the checksum
 * mandated by the EMV-MPM / BR Code spec (field 63). The payload is ASCII-only
 * by construction, so charCodeAt equals the UTF-8 byte.
 */
export function crc16ccitt(payload: string): string {
  let crc = 0xffff;
  for (let i = 0; i < payload.length; i += 1) {
    crc ^= payload.charCodeAt(i) << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}

function isValidCpf(digits: string): boolean {
  if (!/^\d{11}$/.test(digits)) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;
  const checkDigit = (len: number): number => {
    let sum = 0;
    for (let i = 0; i < len; i += 1) sum += Number(digits[i]) * (len + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return checkDigit(9) === Number(digits[9]) && checkDigit(10) === Number(digits[10]);
}

/**
 * Normalize a clipper-entered free-text PIX key into the form the BR Code
 * payload expects, or null when no plausible key can be derived. The
 * CPF-vs-phone call for bare 11-digit strings uses CPF check digits; the raw
 * key is always displayed beside the QR so the admin can fall back to a
 * manual transfer if the heuristic ever guesses wrong.
 */
export function normalizePixKey(raw: string): string | null {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;
  if (trimmed.includes('@')) return trimmed.toLowerCase();
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) {
    return trimmed.toLowerCase();
  }
  if (trimmed.startsWith('+')) {
    const digits = trimmed.replace(/\D/g, '');
    return digits ? `+${digits}` : null;
  }
  const digits = trimmed.replace(/[.\-/() ]/g, '');
  if (!/^\d+$/.test(digits)) return null;
  if (digits.length === 14) return digits;
  if (digits.length === 11) return isValidCpf(digits) ? digits : `+55${digits}`;
  if (digits.length === 10) return `+55${digits}`;
  return null;
}

function normalizeReceiverName(name: string): string {
  const ascii = (name || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\x20-\x7e]/g, '')
    .trim()
    .slice(0, 25)
    .trim();
  return ascii || NAME_FALLBACK;
}

/**
 * Assemble a static PIX BR Code (EMV-MPM) payload: scanning it in a banking
 * app pre-fills a transfer of `amountCents` to `pixKey`. Name/city fields are
 * display-only — the payer's bank shows the key owner's registered name from
 * the central directory before confirming.
 */
export function buildPixBrCode({
  pixKey,
  amountCents,
  receiverName,
  txid = '***',
}: {
  pixKey: string;
  amountCents: number;
  receiverName: string;
  txid?: string;
}): string {
  const merchantAccountInfo = emv('00', PIX_GUI) + emv('01', pixKey);
  const amount = (amountCents / 100).toFixed(2);
  const payload = `${[
    emv('00', '01'),
    emv('26', merchantAccountInfo),
    emv('52', '0000'),
    emv('53', '986'),
    emv('54', amount),
    emv('58', 'BR'),
    emv('59', normalizeReceiverName(receiverName)),
    emv('60', MERCHANT_CITY),
    emv('62', emv('05', txid)),
  ].join('')}6304`;
  return payload + crc16ccitt(payload);
}
